import { pickCoachControlTargetIndex } from "./coachExerciseNavigation";

export function slotHasSeatedPlayer(slot) {
  return (
    slot != null &&
    typeof slot === "object" &&
    Object.keys(slot).length > 0 &&
    (slot.player_id ||
      slot.playerId ||
      slot.user_id ||
      slot.userId ||
      slot.user_first_name ||
      slot.user_last_name ||
      slot.user_alias)
  );
}

function positionIndex(player) {
  const raw = player?.player_position || player?.position || "";
  const n = parseInt(String(raw).replace(/\D/g, ""), 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/** Ordered seats from canonical `players[]`, else legacy player1/2/3. */
export function orderedSeatedPlayers(table) {
  if (!table || typeof table !== "object") return [];
  if (Array.isArray(table.players) && table.players.length > 0) {
    return table.players
      .filter(slotHasSeatedPlayer)
      .sort((a, b) => positionIndex(a) - positionIndex(b) || 0);
  }
  return [table.player1, table.player2, table.player3].filter(slotHasSeatedPlayer);
}

export function seatedPlayerIds(table) {
  return orderedSeatedPlayers(table)
    .map((p) => p.player_id ?? p.playerId)
    .filter((id) => id != null && id !== "")
    .map(String);
}

export function extraSeatedPlayers(table) {
  return orderedSeatedPlayers(table).slice(3);
}

export function mapTableIdToSubgroup(subgroups) {
  const map = new Map();
  for (const sg of subgroups || []) {
    for (const t of sg.tables || []) {
      if (t?.table_id) map.set(String(t.table_id), sg);
    }
  }
  return map;
}

export function subgroupForTable(table, subgroupByTableId) {
  if (!table) return null;
  if (table.tsg_id) {
    return {
      tsg_id: table.tsg_id,
      name: table.tsg_name,
      color: table.tsg_color,
      sort_order: table.tsg_sort_order,
      is_main: table.tsg_is_main,
    };
  }
  return subgroupByTableId?.get(String(table.table_id)) || null;
}

export function sortTablesByStation(tables, subgroupByTableId) {
  return [...(tables || [])].sort((a, b) => {
    const sa = subgroupForTable(a, subgroupByTableId);
    const sb = subgroupForTable(b, subgroupByTableId);
    const oa = sa?.sort_order ?? (sa?.is_main ? -1 : 999);
    const ob = sb?.sort_order ?? (sb?.is_main ? -1 : 999);
    if (oa !== ob) return oa - ob;
    return (Number(a.table_number) || 0) - (Number(b.table_number) || 0);
  });
}

export function findViewerSubgroupId({
  tables = [],
  subgroups = [],
  userId = null,
  playerId = null,
} = {}) {
  const subgroupByTableId = mapTableIdToSubgroup(subgroups);
  const uid = userId != null ? String(userId) : null;
  const pid = playerId != null ? String(playerId) : null;
  for (const table of tables || []) {
    const seated = orderedSeatedPlayers(table);
    const hit = seated.some((p) => {
      const seatPid = p.player_id != null ? String(p.player_id) : p.playerId != null ? String(p.playerId) : null;
      const seatUid = p.user_id != null ? String(p.user_id) : p.userId != null ? String(p.userId) : null;
      return (pid && seatPid === pid) || (uid && seatUid === uid);
    });
    if (hit) {
      return (
        table.tsg_id ||
        subgroupByTableId.get(String(table.table_id))?.tsg_id ||
        null
      );
    }
  }
  return null;
}

export function pickStationScopedExerciseIndex(
  exercises,
  realtimeByTeId = {},
  tsgId = null
) {
  const list = exercises || [];
  if (!tsgId) return pickCoachControlTargetIndex(list, realtimeByTeId);
  const scoped = list.filter((e) => String(e.tsg_id || "") === String(tsgId));
  // Stay on the focused station only — do not jump back to the main group.
  if (!scoped.length) return -1;
  // pickCoachControlTargetIndex sorts a copy; resolve by te_id back into `list`.
  const ordered = [...scoped].sort(
    (a, b) => (a.te_order ?? 0) - (b.te_order ?? 0)
  );
  const local = pickCoachControlTargetIndex(ordered, realtimeByTeId);
  const teId = ordered[local]?.te_id ?? ordered[0]?.te_id;
  if (teId == null) return -1;
  const idx = list.findIndex((e) => String(e.te_id) === String(teId));
  return idx;
}

export function sanitizePlayersArray(players, slotOk) {
  if (!Array.isArray(players)) return players;
  return players.map((p) => slotOk(p)).filter(Boolean);
}
